import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import QRCode from 'react-qr-code';
import Layout from '../components/Layout';
import canteenService from '../services/canteenService';
import {
  Wallet, ShoppingCart, Plus, Minus, X, CheckCircle, Clock, ChefHat,
  Bell, XCircle, WarningCircle, Receipt, ArrowClockwise,
} from '@phosphor-icons/react';

function formatCurrency(amount) {
  return `GHS ${Number(amount || 0).toLocaleString('en-GH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const STATUS_META = {
  pending: { label: 'Waiting for the canteen to accept', color: '#d97706', bg: '#fef3c7', icon: Clock },
  accepted: { label: 'Accepted — will start preparing soon', color: '#2563eb', bg: '#eff6ff', icon: CheckCircle },
  preparing: { label: 'Being prepared', color: '#7c3aed', bg: '#f5f3ff', icon: ChefHat },
  ready: { label: 'Ready for pickup!', color: '#16a34a', bg: '#f0fdf4', icon: Bell },
  completed: { label: 'Collected', color: '#16a34a', bg: '#f0fdf4', icon: CheckCircle },
  rejected: { label: 'Rejected by the canteen', color: '#dc2626', bg: '#fef2f2', icon: XCircle },
  cancelled: { label: 'Cancelled — refunded', color: '#6b7280', bg: '#f3f4f6', icon: XCircle },
};

function titleCase(s) {
  return (s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function CanteenPage() {
  const [tab, setTab] = useState('menu'); // menu | orders
  const [wallet, setWallet] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState({}); // { item_id: quantity }
  const [cartOpen, setCartOpen] = useState(false);
  const [note, setNote] = useState('');
  const [placing, setPlacing] = useState(false);
  const [confirmedOrder, setConfirmedOrder] = useState(null);

  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [expandedOrderId, setExpandedOrderId] = useState(null);

  const loadWallet = useCallback(() => {
    canteenService.getStudentSummary()
      .then(setWallet)
      .catch(() => toast.error('Failed to load wallet balance'));
  }, []);

  const loadItems = useCallback(() => {
    canteenService.listItems()
      .then((data) => setItems(Array.isArray(data) ? data : []))
      .catch(() => toast.error('Failed to load canteen menu'))
      .finally(() => setLoading(false));
  }, []);

  const loadOrders = useCallback(() => {
    setOrdersLoading(true);
    canteenService.listOrders({ limit: 50 })
      .then((data) => setOrders(Array.isArray(data) ? data : []))
      .catch(() => toast.error('Failed to load your orders'))
      .finally(() => setOrdersLoading(false));
  }, []);

  useEffect(() => { loadWallet(); loadItems(); }, [loadWallet, loadItems]);
  useEffect(() => { if (tab === 'orders') loadOrders(); }, [tab, loadOrders]);

  const blockedCategories = wallet?.blocked_categories || [];

  const itemsByCategory = useMemo(() => {
    const groups = {};
    for (const item of items) {
      const key = item.item_type || 'other';
      if (!groups[key]) groups[key] = [];
      groups[key].push(item);
    }
    return groups;
  }, [items]);

  const cartLines = useMemo(() => {
    return Object.entries(cart)
      .filter(([, qty]) => qty > 0)
      .map(([itemId, qty]) => {
        const item = items.find((i) => i.id === itemId);
        return item ? { item, quantity: qty } : null;
      })
      .filter(Boolean);
  }, [cart, items]);

  const cartTotal = cartLines.reduce((sum, l) => sum + l.item.price * l.quantity, 0);
  const cartCount = cartLines.reduce((sum, l) => sum + l.quantity, 0);

  const addToCart = (item) => {
    if (blockedCategories.includes(item.item_type)) {
      toast.error(`${item.name} is blocked by a parent spending control`);
      return;
    }
    if (item.stock_count !== null && item.stock_count !== undefined && item.stock_count <= 0) {
      toast.error(`${item.name} is out of stock`);
      return;
    }
    setCart((prev) => ({ ...prev, [item.id]: (prev[item.id] || 0) + 1 }));
  };

  const changeQty = (itemId, delta) => {
    setCart((prev) => {
      const next = Math.max(0, (prev[itemId] || 0) + delta);
      return { ...prev, [itemId]: next };
    });
  };

  const handlePlaceOrder = async () => {
    if (cartLines.length === 0) return;
    setPlacing(true);
    try {
      const response = await canteenService.placeOrder({
        items: cartLines.map((l) => ({ item_id: l.item.id, quantity: l.quantity })),
        note: note.trim() || undefined,
      });
      setConfirmedOrder(response.order);
      setCart({});
      setNote('');
      setCartOpen(false);
      loadWallet();
      loadItems();
      toast.success(`Order ${response.order.order_code} placed!`);
    } catch (error) {
      toast.error(error?.message || 'Could not place order');
    } finally {
      setPlacing(false);
    }
  };

  const handleCancelOrder = async (orderId) => {
    try {
      await canteenService.cancelOrder(orderId);
      toast.success('Order cancelled and refunded');
      loadOrders();
      loadWallet();
    } catch (error) {
      toast.error(error?.message || 'Could not cancel order');
    }
  };

  return (
    <Layout>
      <div className="page" style={{ display: 'grid', gap: 20 }}>
        <div className="page-header">
          <div>
            <h1>Canteen</h1>
            <p>Order food and snacks with your prepaid wallet</p>
          </div>
        </div>

        {/* Wallet balance */}
        <div className="card" style={{ padding: 20, background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)', color: 'white' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 14, opacity: 0.9 }}>Wallet Balance</div>
              <div style={{ fontSize: 32, fontWeight: 700 }}>{formatCurrency(wallet?.balance || 0)}</div>
              {wallet?.frozen && (
                <div style={{ marginTop: 6, fontSize: 13, background: 'rgba(255,255,255,0.2)', display: 'inline-block', padding: '4px 10px', borderRadius: 8 }}>
                  Wallet frozen — ask a parent to unfreeze it
                </div>
              )}
              {!wallet?.frozen && wallet?.low_balance && (
                <div style={{ marginTop: 6, fontSize: 13, background: 'rgba(255,255,255,0.2)', display: 'inline-block', padding: '4px 10px', borderRadius: 8 }}>
                  Low balance — ask a parent to top up
                </div>
              )}
            </div>
            <div style={{ padding: 12, borderRadius: '50%', background: 'rgba(255,255,255,0.18)' }}>
              <Wallet size={28} />
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 8, borderBottom: '1px solid #e5e7eb' }}>
          <button
            className={`tab-btn ${tab === 'menu' ? 'active' : ''}`}
            onClick={() => setTab('menu')}
          >
            Menu
          </button>
          <button
            className={`tab-btn ${tab === 'orders' ? 'active' : ''}`}
            onClick={() => setTab('orders')}
          >
            <Receipt size={16} /> My Orders
          </button>
        </div>

        {tab === 'menu' && (
          <div style={{ display: 'grid', gap: 20, paddingBottom: cartCount > 0 ? 70 : 0 }}>
            {loading ? (
              <div style={{ color: '#6b7280' }}>Loading menu...</div>
            ) : items.length === 0 ? (
              <div className="card" style={{ padding: 20, color: '#6b7280' }}>No items on the menu yet.</div>
            ) : (
              Object.entries(itemsByCategory).map(([category, categoryItems]) => (
                <div key={category}>
                  <h3 style={{ marginBottom: 10 }}>{titleCase(category)}</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
                    {categoryItems.map((item) => {
                      const blocked = blockedCategories.includes(item.item_type);
                      const outOfStock = item.stock_count !== null && item.stock_count !== undefined && item.stock_count <= 0;
                      const disabled = blocked || outOfStock;
                      const qtyInCart = cart[item.id] || 0;
                      return (
                        <div key={item.id} className="card" style={{ padding: 16, opacity: disabled ? 0.6 : 1 }}>
                          <div style={{ fontWeight: 700 }}>{item.name}</div>
                          {item.info && <div style={{ color: '#6b7280', fontSize: 13, marginTop: 2 }}>{item.info}</div>}
                          <div style={{ marginTop: 8, color: '#2563eb', fontWeight: 700 }}>{formatCurrency(item.price)}</div>
                          <div style={{ fontSize: 11, color: item.needs_prep ? '#7c3aed' : '#16a34a', marginTop: 4 }}>
                            {item.needs_prep ? 'Made to order — a short wait' : 'Ready for pickup right away'}
                          </div>
                          {outOfStock && <div style={{ fontSize: 12, color: '#dc2626', marginTop: 4 }}>Out of stock</div>}
                          {blocked && !outOfStock && <div style={{ fontSize: 12, color: '#dc2626', marginTop: 4 }}>Blocked by parent control</div>}
                          {item.stock_count !== null && item.stock_count !== undefined && !outOfStock && item.stock_count <= 5 && (
                            <div style={{ fontSize: 12, color: '#d97706', marginTop: 4 }}>Only {item.stock_count} left</div>
                          )}
                          <div style={{ marginTop: 10 }}>
                            {qtyInCart > 0 ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <button className="btn btn-secondary" style={{ padding: '4px 10px' }} onClick={() => changeQty(item.id, -1)}><Minus size={14} /></button>
                                <span style={{ fontWeight: 700 }}>{qtyInCart}</span>
                                <button className="btn btn-secondary" style={{ padding: '4px 10px' }} onClick={() => changeQty(item.id, 1)} disabled={disabled}><Plus size={14} /></button>
                              </div>
                            ) : (
                              <button className="btn btn-primary" style={{ width: '100%' }} disabled={disabled} onClick={() => addToCart(item)}>
                                <Plus size={14} /> Add
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {tab === 'orders' && (
          <div style={{ display: 'grid', gap: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={loadOrders}><ArrowClockwise size={14} /> Refresh</button>
            </div>
            {ordersLoading ? (
              <div style={{ color: '#6b7280' }}>Loading orders...</div>
            ) : orders.length === 0 ? (
              <div className="card" style={{ padding: 20, color: '#6b7280' }}>No orders yet.</div>
            ) : (
              orders.map((order) => {
                const meta = STATUS_META[order.status] || STATUS_META.pending;
                const StatusIcon = meta.icon;
                const expanded = expandedOrderId === order.id;
                return (
                  <div key={order.id} className="card" style={{ padding: 16 }}>
                    <div
                      style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                      onClick={() => setExpandedOrderId(expanded ? null : order.id)}
                    >
                      <div>
                        <div style={{ fontWeight: 700 }}>{order.order_code}</div>
                        <div style={{ color: '#6b7280', fontSize: 13 }}>
                          {order.items.map((l) => `${l.quantity}x ${l.name}`).join(', ')}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontWeight: 700 }}>{formatCurrency(order.total)}</div>
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: meta.color, background: meta.bg, padding: '2px 8px', borderRadius: 8, marginTop: 4 }}>
                          <StatusIcon size={12} /> {titleCase(order.status)}
                        </div>
                      </div>
                    </div>

                    {expanded && (
                      <div style={{ marginTop: 14, borderTop: '1px solid #e5e7eb', paddingTop: 14 }}>
                        <div style={{ fontSize: 13, color: meta.color, fontWeight: 600, marginBottom: 10 }}>{meta.label}</div>

                        {order.status === 'ready' && (
                          <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: 16, textAlign: 'center', marginBottom: 12 }}>
                            <div style={{ fontSize: 12, color: '#15803d', fontWeight: 700, marginBottom: 8 }}>
                              Show this code at the counter
                            </div>
                            <div style={{ background: 'white', display: 'inline-block', padding: 10, borderRadius: 8 }}>
                              <QRCode value={order.pickup_code} size={140} level="H" />
                            </div>
                            <div style={{ marginTop: 8, fontSize: 20, fontWeight: 800, letterSpacing: 4 }}>{order.pickup_code}</div>
                          </div>
                        )}

                        {order.rejection_reason && (
                          <div style={{ fontSize: 13, color: '#dc2626', marginBottom: 10 }}>
                            <WarningCircle size={14} /> {order.rejection_reason}
                          </div>
                        )}

                        {order.note && (
                          <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 10 }}>Note: {order.note}</div>
                        )}

                        {(order.status === 'pending' || (order.status === 'ready' && !order.accepted_at && !order.preparing_at)) && (
                          <button className="btn btn-secondary" onClick={() => handleCancelOrder(order.id)}>
                            <X size={14} /> Cancel order
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Floating cart bar */}
        {tab === 'menu' && cartCount > 0 && (
          <div style={{ position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 40 }}>
            <button
              className="btn btn-primary"
              style={{ padding: '14px 24px', borderRadius: 30, boxShadow: '0 8px 24px rgba(0,0,0,0.2)', display: 'flex', alignItems: 'center', gap: 10 }}
              onClick={() => setCartOpen(true)}
            >
              <ShoppingCart size={18} /> {cartCount} item{cartCount !== 1 ? 's' : ''} · {formatCurrency(cartTotal)}
            </button>
          </div>
        )}

        {/* Cart modal */}
        {cartOpen && (
          <div className="sec-modal-overlay" onClick={() => setCartOpen(false)}>
            <div className="sec-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <p className="sec-modal-title" style={{ margin: 0 }}>Your Order</p>
                <button className="btn btn-secondary" style={{ padding: '4px 8px' }} onClick={() => setCartOpen(false)}><X size={16} /></button>
              </div>

              <div style={{ display: 'grid', gap: 10, marginBottom: 14 }}>
                {cartLines.map((line) => (
                  <div key={line.item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontWeight: 600 }}>{line.item.name}</div>
                      <div style={{ fontSize: 12, color: '#6b7280' }}>{formatCurrency(line.item.price)} each</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <button className="btn btn-secondary" style={{ padding: '2px 8px' }} onClick={() => changeQty(line.item.id, -1)}><Minus size={12} /></button>
                      <span>{line.quantity}</span>
                      <button className="btn btn-secondary" style={{ padding: '2px 8px' }} onClick={() => changeQty(line.item.id, 1)}><Plus size={12} /></button>
                    </div>
                  </div>
                ))}
              </div>

              <textarea
                className="form-control"
                placeholder="Note for the canteen (optional)"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                style={{ marginBottom: 14, minHeight: 60 }}
              />

              <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 12, marginBottom: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#6b7280' }}>
                  <span>Balance before</span><span>{formatCurrency(wallet?.balance || 0)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, marginTop: 4 }}>
                  <span>Total</span><span>{formatCurrency(cartTotal)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#6b7280', marginTop: 4 }}>
                  <span>Balance after</span><span>{formatCurrency((wallet?.balance || 0) - cartTotal)}</span>
                </div>
              </div>

              <button className="btn btn-primary" style={{ width: '100%' }} disabled={placing} onClick={handlePlaceOrder}>
                {placing ? 'Placing order...' : 'Place Order'}
              </button>
            </div>
          </div>
        )}

        {/* Order confirmation */}
        {confirmedOrder && (
          <div className="sec-modal-overlay" onClick={() => setConfirmedOrder(null)}>
            <div className="sec-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 380, textAlign: 'center' }}>
              <CheckCircle size={48} weight="fill" color="#16a34a" />
              <p className="sec-modal-title" style={{ marginTop: 10 }}>Order Placed!</p>
              <p style={{ color: '#6b7280', fontSize: 13, marginBottom: confirmedOrder.status === 'ready' ? 16 : 4 }}>
                {confirmedOrder.order_code} · {formatCurrency(confirmedOrder.total)}
              </p>

              {confirmedOrder.status === 'ready' ? (
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: 16, marginBottom: 16 }}>
                  <div style={{ fontSize: 12, color: '#15803d', fontWeight: 700, marginBottom: 8 }}>
                    Ready now — show this code at the counter
                  </div>
                  <div style={{ background: 'white', display: 'inline-block', padding: 10, borderRadius: 8 }}>
                    <QRCode value={confirmedOrder.pickup_code} size={140} level="H" />
                  </div>
                  <div style={{ marginTop: 8, fontSize: 20, fontWeight: 800, letterSpacing: 4 }}>{confirmedOrder.pickup_code}</div>
                </div>
              ) : (
                <p style={{ color: '#6b7280', fontSize: 13, marginBottom: 16 }}>
                  Check "My Orders" once it's ready for pickup — you'll get a code to show at the counter.
                </p>
              )}

              <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => { setConfirmedOrder(null); setTab('orders'); }}>
                View My Orders
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
